import React, {useState, useMemo} from "react";
import {Column, Row, Text} from "react-vcomponents";
import {ShowMessageBox} from "react-vmessagebox";
import {observer_mgl} from "mobx-graphlink";
import {DialogStyle} from "../../../../../Utils/UI/GlobalStyles.js";
import {MapObject} from "../../../../../Server/DB/mapObjects/@MapObject.js";
import {GetObjDescendants, ResolveObjPosInWorld} from "../../../../../Server/DB/mapObjects/$mapObject.js";
import {ToJSON_Compact} from "../../../../../Utils/General/Serialization.js";
import {GetCameraViewJSON, ObjExportJSONFixturePanel} from "./ObjExportDialog_Base.js";

/** Opens the subtree-export dialog for the right-clicked outliner object (exports the object
 *  itself + all of its descendants, as raw JSON). */
export function ShowSubtreeExportDialog(obj: MapObject, mapID: string) {
	ShowMessageBox({
		title: "Export subtree for unit-tests",
		cancelButton: true,
		okButton: false,
		message: ()=><SubtreeExportDialogUI obj={obj} mapID={mapID}/>,
	});
}

const SubtreeExportDialogUI = observer_mgl(({obj, mapID}: {obj: MapObject, mapID: string})=>{
	const [includeCamView, setIncludeCamView] = useState(true);

	const objects = useMemo(()=>[obj, ...GetObjDescendants(obj)], [obj]);

	const json = useMemo(()=>{
		// Raw object JSONs (as-stored; not resolved via ResolveMapObjectsForTerrain), except we
		// do inject the `_extras.resolvedWorldPos` substructure, since fixture tests key off it.
		const exportedObjects = objects.map(o=>{
			// JSON round-trip converts class instances (Vec3, etc.) into plain JSON structures.
			const plain = JSON.parse(JSON.stringify(o)) as Record<string, any>;
			const pos = ResolveObjPosInWorld(o);
			plain._extras = {...plain._extras, resolvedWorldPos: pos ? {x: pos.x, y: pos.y, z: pos.z ?? 0} : undefined};
			return plain;
		});

		const cameraViewJSON = GetCameraViewJSON(mapID, includeCamView);

		return cameraViewJSON
			? ToJSON_Compact({cameraView: cameraViewJSON, objects: exportedObjects})
			: ToJSON_Compact({objects: exportedObjects});
	}, [objects, includeCamView, mapID]);

	return (
		<Column style={DialogStyle({width: 700})}>
			<Row mt={5}>
				<Text style={{fontWeight: "bold"}}>
					Exporting {objects.length} object{objects.length !== 1 ? "s" : ""} (the right-clicked object + all descendants):
				</Text>
			</Row>
			<Column mt={5} style={{
				maxHeight: 160,
				overflowY: "auto",
				background: "rgba(0,0,0,0.2)",
				borderRadius: 4,
				padding: "4px 8px",
			}}>
				{objects.map(o=>(
					<Row key={o.id} style={{fontSize: 12, padding: "1px 0"}}>
						<Text style={{minWidth: 120, fontFamily: "monospace", opacity: 0.7}}>{o.id.slice(0, 8)}…</Text>
						<Text style={{fontWeight: "bold"}}>{o.name}</Text>
					</Row>
				))}
			</Column>

			<ObjExportJSONFixturePanel json={json} includeCamView={includeCamView} setIncludeCamView={setIncludeCamView}
				helpText={[
					"Paste this JSON as the fixture in a new test case.",
					"The `objects` array holds the raw object JSONs (as stored, not resolved through the terrain pipeline), each with an injected `_extras.resolvedWorldPos` = its resolved world position.",
					"The `cameraView` field is the current camera view-rect (minX/minY/maxX/maxY in world coords).",
					"Ask an LLM to write the assertions based on your description of the observed issue.",
				].join(" ")}/>
		</Column>
	);
});
